import React, { useState, useEffect, useCallback } from 'react';
import {
  Wallet, Search, RefreshCw, Filter, CheckCircle, XCircle,
  Clock, AlertTriangle, Download, Eye, ChevronLeft, ChevronRight,
  X, DollarSign, TrendingUp, Calendar, User, CreditCard, Ban,
  FileText, Hash
} from 'lucide-react';
import { formatDistanceToNow, format } from 'date-fns';
import { adminService } from '../services/adminService';
import { exportToCSV, exportToPDF, formatDateForExport, formatCurrencyForExport } from '../utils/exportUtils';
import { useToast } from '../contexts/ToastContext';
import './Withdrawals.css';

type WithdrawalStatus = 'pending' | 'approved' | 'processing' | 'completed' | 'rejected' | 'failed' | 'cancelled';

interface Withdrawal {
  withdrawal_id: number;
  id?: number;
  user_id?: number;
  reference: string;
  amount: number;
  fee_amount: number;
  net_amount: number;
  currency_code: string;
  method: string;
  status: WithdrawalStatus;
  requested_at: string;
  created_at?: string;
  processed_at?: string;
  updated_at?: string;
  bank_name?: string;
  account_number?: string;
  account_name?: string;
  bank_code?: string;
  sort_code?: string;
  rejection_reason?: string;
  failure_reason?: string;
  user_email?: string;
  user_full_name?: string;
}

function normalizeWithdrawal(raw: any): Withdrawal {
  return {
    withdrawal_id: raw.withdrawal_id ?? raw.id ?? 0,
    id: raw.id ?? raw.withdrawal_id,
    user_id: raw.user_id,
    reference: raw.reference ?? '',
    amount: Number(raw.amount ?? 0),
    fee_amount: Number(raw.fee_amount ?? 0),
    net_amount: Number(raw.net_amount ?? 0),
    currency_code: raw.currency_code ?? 'NGN',
    method: raw.method ?? '',
    status: (raw.status ?? 'pending').toLowerCase() as WithdrawalStatus,
    requested_at: raw.requested_at ?? raw.created_at ?? '',
    created_at: raw.created_at ?? raw.requested_at,
    processed_at: raw.processed_at,
    updated_at: raw.updated_at,
    bank_name: raw.bank_name,
    account_number: raw.account_number,
    account_name: raw.account_name,
    bank_code: raw.bank_code,
    sort_code: raw.sort_code,
    rejection_reason: raw.rejection_reason ?? raw.failure_reason,
    failure_reason: raw.failure_reason,
    user_email: raw.user_email,
    user_full_name: raw.user_full_name,
  };
}

function extractArray(response: any): any[] {
  if (Array.isArray(response)) return response;
  if (response?.withdrawals && Array.isArray(response.withdrawals)) return response.withdrawals;
  if (response?.data && Array.isArray(response.data)) return response.data;
  return [];
}

const PAGE_SIZE = 20;

const Withdrawals: React.FC = () => {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<Withdrawal | null>(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showConfirmApprove, setShowConfirmApprove] = useState<Withdrawal | null>(null);
  const toast = useToast();

  const [stats, setStats] = useState({
    totalCount: 0,
    pendingCount: 0,
    approvedProcessingCount: 0,
    totalAmount: 0,
  });

  const computeStats = useCallback((allItems: Withdrawal[]) => {
    const pending = allItems.filter(w => w.status === 'pending');
    const approvedOrProcessing = allItems.filter(w => w.status === 'approved' || w.status === 'processing');
    setStats({
      totalCount: allItems.length,
      pendingCount: pending.length,
      approvedProcessingCount: approvedOrProcessing.length,
      totalAmount: allItems.reduce((sum, w) => sum + w.amount, 0),
    });
  }, []);

  const loadWithdrawals = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      };
      if (statusFilter) {
        params.status_filter = statusFilter;
      }

      const response = await adminService.getAllWithdrawals(params);
      const rawList = extractArray(response);
      const normalized = rawList.map(normalizeWithdrawal);

      setWithdrawals(normalized);
      setTotalCount(response?.total ?? rawList.length);

      // Load all withdrawals for stats (unfiltered, first page of a large set)
      if (!statusFilter && page === 0) {
        computeStats(normalized);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to load withdrawals';
      setError(msg);
      toast.error('Load Failed', msg);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, toast, computeStats]);

  // Load stats separately to keep them accurate regardless of pagination/filtering
  const loadStats = useCallback(async () => {
    try {
      const [allResponse, pendingResponse] = await Promise.all([
        adminService.getAllWithdrawals({ skip: 0, limit: 1000 }),
        adminService.getPendingWithdrawals(),
      ]);
      const allItems = extractArray(allResponse).map(normalizeWithdrawal);
      const pendingItems = extractArray(pendingResponse).map(normalizeWithdrawal);
      const approvedOrProcessing = allItems.filter(w => w.status === 'approved' || w.status === 'processing');
      setStats({
        totalCount: allResponse?.total ?? allItems.length,
        pendingCount: pendingItems.length,
        approvedProcessingCount: approvedOrProcessing.length,
        totalAmount: allItems.reduce((sum, w) => sum + w.amount, 0),
      });
    } catch {
      // Stats are non-critical, silently fail
    }
  }, []);

  useEffect(() => {
    loadWithdrawals();
  }, [loadWithdrawals]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  // Reset to page 0 when filter changes
  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  // Client-side search filtering
  const filteredWithdrawals = search
    ? withdrawals.filter(w => {
        const q = search.toLowerCase();
        return (
          w.reference?.toLowerCase().includes(q) ||
          w.user_email?.toLowerCase().includes(q) ||
          w.user_full_name?.toLowerCase().includes(q) ||
          w.account_name?.toLowerCase().includes(q) ||
          w.account_number?.includes(search) ||
          String(w.withdrawal_id).includes(search) ||
          String(w.user_id ?? '').includes(search)
        );
      })
    : withdrawals;

  const handleApprove = async (withdrawal: Withdrawal) => {
    setShowConfirmApprove(null);
    setProcessingId(withdrawal.withdrawal_id);
    try {
      await adminService.updateWithdrawalStatus(withdrawal.withdrawal_id, { status: 'approved' });
      toast.success('Withdrawal Approved', `Withdrawal #${withdrawal.withdrawal_id} approved successfully`);
      loadWithdrawals();
      loadStats();
      if (selectedWithdrawal?.withdrawal_id === withdrawal.withdrawal_id) {
        setSelectedWithdrawal(null);
      }
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to approve withdrawal';
      toast.error('Approval Failed', msg);
    } finally {
      setProcessingId(null);
    }
  };

  const handleReject = async () => {
    if (!selectedWithdrawal || !rejectReason.trim()) {
      toast.error('Error', 'Please provide a rejection reason');
      return;
    }

    setProcessingId(selectedWithdrawal.withdrawal_id);
    try {
      await adminService.updateWithdrawalStatus(selectedWithdrawal.withdrawal_id, {
        status: 'rejected',
        admin_notes: rejectReason,
      });
      toast.warning('Withdrawal Rejected', `Reason: ${rejectReason}`);
      setShowRejectModal(false);
      setRejectReason('');
      setSelectedWithdrawal(null);
      loadWithdrawals();
      loadStats();
    } catch (err: any) {
      const msg = err?.response?.data?.detail || err.message || 'Failed to reject withdrawal';
      toast.error('Rejection Failed', msg);
    } finally {
      setProcessingId(null);
    }
  };

  const openRejectModal = (withdrawal: Withdrawal) => {
    setSelectedWithdrawal(withdrawal);
    setShowRejectModal(true);
  };

  const formatAmount = (amount: number, currency: string) => {
    const symbols: Record<string, string> = { NGN: '₦', USD: '$', GBP: '£', GHS: '₵', CAD: 'C$' };
    const symbol = symbols[currency?.toUpperCase()] || currency + ' ';
    return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatMethod = (method: string) => {
    const labels: Record<string, string> = {
      bank_transfer: 'Bank Transfer',
      mobile_money: 'Mobile Money',
      digital_wallet: 'Digital Wallet',
    };
    return labels[method] || method || '—';
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return <Clock size={14} />;
      case 'approved':
      case 'processing': return <RefreshCw size={14} className="spin" />;
      case 'completed': return <CheckCircle size={14} />;
      case 'rejected':
      case 'cancelled': return <XCircle size={14} />;
      case 'failed': return <AlertTriangle size={14} />;
      default: return null;
    }
  };

  const exportColumns = [
    { key: 'withdrawal_id', header: 'ID' },
    { key: 'reference', header: 'Reference' },
    { key: 'user_full_name', header: 'User' },
    { key: 'user_email', header: 'Email' },
    { key: 'amount', header: 'Amount', formatter: (v: number) => formatCurrencyForExport(v, '₦') },
    { key: 'fee_amount', header: 'Fee', formatter: (v: number) => formatCurrencyForExport(v, '₦') },
    { key: 'net_amount', header: 'Net Amount', formatter: (v: number) => formatCurrencyForExport(v, '₦') },
    { key: 'currency_code', header: 'Currency' },
    { key: 'method', header: 'Method', formatter: formatMethod },
    { key: 'status', header: 'Status' },
    { key: 'account_number', header: 'Account' },
    { key: 'requested_at', header: 'Requested', formatter: formatDateForExport },
    { key: 'processed_at', header: 'Processed', formatter: formatDateForExport },
  ];

  const handleExportCSV = () => {
    exportToCSV(filteredWithdrawals, exportColumns, 'withdrawals_export');
    toast.success('Export Complete', 'Withdrawals exported to CSV');
    setShowExportMenu(false);
  };

  const handleExportPDF = () => {
    exportToPDF(filteredWithdrawals, exportColumns, 'Withdrawal Report', 'withdrawals_report');
    toast.success('Export Complete', 'PDF report generated');
    setShowExportMenu(false);
  };

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  return (
    <div className="withdrawals-page">
      {/* Header */}
      <div className="wd-header">
        <div className="header-content">
          <h1><Wallet size={28} /> Withdrawals</h1>
          <p className="header-subtitle">Manage and process withdrawal requests</p>
        </div>
        <div className="header-actions">
          <button onClick={() => { loadWithdrawals(); loadStats(); }} className="refresh-btn" disabled={loading}>
            <RefreshCw className={loading ? 'spin' : ''} size={18} />
            Refresh
          </button>
          <div className="export-dropdown">
            <button className="export-btn" onClick={() => setShowExportMenu(!showExportMenu)}>
              <Download size={18} />
              Export
            </button>
            {showExportMenu && (
              <div className="export-menu">
                <button onClick={handleExportCSV}>
                  <FileText size={16} /> Export as CSV
                </button>
                <button onClick={handleExportPDF}>
                  <FileText size={16} /> Export as PDF
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="wd-stats">
        <div className="stat-card total-card">
          <div className="stat-icon">
            <Wallet size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.totalCount}</span>
            <span className="stat-label">Total Withdrawals</span>
          </div>
        </div>
        <div className="stat-card pending-card">
          <div className="stat-icon">
            <Clock size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.pendingCount}</span>
            <span className="stat-label">Pending</span>
          </div>
        </div>
        <div className="stat-card processing-card">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">{stats.approvedProcessingCount}</span>
            <span className="stat-label">Approved / Processing</span>
          </div>
        </div>
        <div className="stat-card amount-card">
          <div className="stat-icon">
            <DollarSign size={24} />
          </div>
          <div className="stat-content">
            <span className="stat-value">₦{stats.totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            <span className="stat-label">Total Amount</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search by name, email, reference, ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="clear-search" onClick={() => setSearch('')}>
              <X size={16} />
            </button>
          )}
        </div>
        <div className="filter-group">
          <Filter size={16} />
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="rejected">Rejected</option>
            <option value="failed">Failed</option>
          </select>
        </div>
      </div>

      {/* Error State */}
      {error && !loading && (
        <div className="error-state">
          <AlertTriangle size={32} />
          <h3>Failed to load withdrawals</h3>
          <p>{error}</p>
          <button onClick={loadWithdrawals} className="retry-btn">
            <RefreshCw size={16} /> Retry
          </button>
        </div>
      )}

      {/* Withdrawals Table */}
      <div className="wd-table-container">
        {loading ? (
          <div className="loading-state">
            <RefreshCw className="spin" size={32} />
            <span>Loading withdrawals...</span>
          </div>
        ) : !error ? (
          <>
            <table className="wd-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>User</th>
                  <th>Amount</th>
                  <th>Bank / Account</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredWithdrawals.map((wd) => (
                  <tr
                    key={wd.withdrawal_id}
                    className={wd.status === 'pending' ? 'highlight-row' : ''}
                    onClick={() => { if (!showRejectModal && !showConfirmApprove) setSelectedWithdrawal(wd); }}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <div className="id-cell">
                        <span className="reference">{wd.reference || `#${wd.withdrawal_id}`}</span>
                        <span className="id-sub">ID: {wd.withdrawal_id}</span>
                      </div>
                    </td>
                    <td>
                      <div className="user-cell">
                        <span className="user-name">{wd.user_full_name || `User #${wd.user_id ?? '?'}`}</span>
                        <span className="user-email">{wd.user_email || ''}</span>
                      </div>
                    </td>
                    <td>
                      <div className="amount-cell">
                        <span className="amount">{formatAmount(wd.amount, wd.currency_code)}</span>
                        <span className="net-amount">Net: {formatAmount(wd.net_amount, wd.currency_code)}</span>
                      </div>
                    </td>
                    <td>
                      <div className="bank-cell">
                        <span className="bank-name">{wd.bank_name || formatMethod(wd.method)}</span>
                        <span className="account-info">
                          {wd.account_number ? `${wd.account_number}` : '—'}
                          {wd.account_name ? ` • ${wd.account_name}` : ''}
                        </span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${wd.status}`}>
                        {getStatusIcon(wd.status)}
                        {wd.status}
                      </span>
                    </td>
                    <td className="date-cell">
                      {wd.requested_at
                        ? formatDistanceToNow(new Date(wd.requested_at), { addSuffix: true })
                        : '—'}
                    </td>
                    <td>
                      <div className="action-buttons" onClick={(e) => e.stopPropagation()}>
                        {wd.status === 'pending' && (
                          <>
                            <button
                              onClick={() => setShowConfirmApprove(wd)}
                              className="action-btn approve"
                              disabled={processingId === wd.withdrawal_id}
                              title="Approve"
                            >
                              {processingId === wd.withdrawal_id ? (
                                <RefreshCw size={16} className="spin" />
                              ) : (
                                <CheckCircle size={16} />
                              )}
                            </button>
                            <button
                              onClick={() => openRejectModal(wd)}
                              className="action-btn reject"
                              disabled={processingId === wd.withdrawal_id}
                              title="Reject"
                            >
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => setSelectedWithdrawal(wd)}
                          className="action-btn view"
                          title="View details"
                        >
                          <Eye size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filteredWithdrawals.length === 0 && (
              <div className="no-data">
                <Wallet size={48} />
                <h3>No withdrawals found</h3>
                <p>Try adjusting your filters or search query</p>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Pagination */}
      {!loading && !error && filteredWithdrawals.length > 0 && (
        <div className="pagination">
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            className="page-btn"
          >
            <ChevronLeft size={18} /> Previous
          </button>
          <span className="page-info">
            Page {page + 1} of {totalPages}
            {totalCount > 0 && <span className="total-records"> ({totalCount} total)</span>}
          </span>
          <button
            onClick={() => setPage(p => p + 1)}
            disabled={page >= totalPages - 1}
            className="page-btn"
          >
            Next <ChevronRight size={18} />
          </button>
        </div>
      )}

      {/* Confirm Approve Dialog */}
      {showConfirmApprove && (
        <div className="modal-overlay" onClick={() => setShowConfirmApprove(null)}>
          <div className="modal-content confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Confirm Approval</h3>
              <button className="modal-close" onClick={() => setShowConfirmApprove(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="confirm-text">
                Approve withdrawal of <strong>{formatAmount(showConfirmApprove.amount, showConfirmApprove.currency_code)}</strong>
                {showConfirmApprove.user_full_name && <> for <strong>{showConfirmApprove.user_full_name}</strong></>}?
              </p>
              <div className="confirm-details">
                <span>Reference: {showConfirmApprove.reference}</span>
                <span>Net: {formatAmount(showConfirmApprove.net_amount, showConfirmApprove.currency_code)}</span>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowConfirmApprove(null)} className="cancel-btn">Cancel</button>
              <button
                onClick={() => handleApprove(showConfirmApprove)}
                className="approve-btn"
                disabled={processingId === showConfirmApprove.withdrawal_id}
              >
                {processingId === showConfirmApprove.withdrawal_id ? (
                  <RefreshCw size={18} className="spin" />
                ) : (
                  <CheckCircle size={18} />
                )}
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Detail Modal */}
      {selectedWithdrawal && !showRejectModal && !showConfirmApprove && (
        <div className="modal-overlay" onClick={() => setSelectedWithdrawal(null)}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Withdrawal Details</h3>
              <button className="modal-close" onClick={() => setSelectedWithdrawal(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="wd-detail-header">
                <div className="wd-amount-display">
                  <span className="currency">{selectedWithdrawal.currency_code === 'NGN' ? '₦' : selectedWithdrawal.currency_code}</span>
                  <span className="amount">{selectedWithdrawal.amount.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                </div>
                <span className={`status-badge large ${selectedWithdrawal.status}`}>
                  {getStatusIcon(selectedWithdrawal.status)}
                  {selectedWithdrawal.status}
                </span>
              </div>

              <div className="detail-section">
                <h4><Hash size={16} /> Withdrawal Info</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Withdrawal ID</span>
                    <span className="detail-value">#{selectedWithdrawal.withdrawal_id}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Reference</span>
                    <span className="detail-value">{selectedWithdrawal.reference || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Method</span>
                    <span className="detail-value">{formatMethod(selectedWithdrawal.method)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Currency</span>
                    <span className="detail-value">{selectedWithdrawal.currency_code}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4><DollarSign size={16} /> Amount Breakdown</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Gross Amount</span>
                    <span className="detail-value">{formatAmount(selectedWithdrawal.amount, selectedWithdrawal.currency_code)}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Fee</span>
                    <span className="detail-value">{formatAmount(selectedWithdrawal.fee_amount, selectedWithdrawal.currency_code)}</span>
                  </div>
                  <div className="detail-item full-width">
                    <span className="detail-label">Net Amount (Payout)</span>
                    <span className="detail-value highlight">{formatAmount(selectedWithdrawal.net_amount, selectedWithdrawal.currency_code)}</span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h4><User size={16} /> User Information</h4>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Name</span>
                    <span className="detail-value">{selectedWithdrawal.user_full_name || '—'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Email</span>
                    <span className="detail-value">{selectedWithdrawal.user_email || '—'}</span>
                  </div>
                  {selectedWithdrawal.user_id && (
                    <div className="detail-item">
                      <span className="detail-label">User ID</span>
                      <span className="detail-value">#{selectedWithdrawal.user_id}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4><CreditCard size={16} /> Bank Details</h4>
                <div className="detail-grid">
                  {selectedWithdrawal.bank_name && (
                    <div className="detail-item">
                      <span className="detail-label">Bank</span>
                      <span className="detail-value">{selectedWithdrawal.bank_name}</span>
                    </div>
                  )}
                  {selectedWithdrawal.account_number && (
                    <div className="detail-item">
                      <span className="detail-label">Account Number</span>
                      <span className="detail-value">{selectedWithdrawal.account_number}</span>
                    </div>
                  )}
                  {selectedWithdrawal.account_name && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Account Name</span>
                      <span className="detail-value">{selectedWithdrawal.account_name}</span>
                    </div>
                  )}
                  {selectedWithdrawal.bank_code && (
                    <div className="detail-item">
                      <span className="detail-label">Bank Code</span>
                      <span className="detail-value">{selectedWithdrawal.bank_code}</span>
                    </div>
                  )}
                  {selectedWithdrawal.sort_code && (
                    <div className="detail-item">
                      <span className="detail-label">Sort Code</span>
                      <span className="detail-value">{selectedWithdrawal.sort_code}</span>
                    </div>
                  )}
                  {!selectedWithdrawal.bank_name && !selectedWithdrawal.account_number && (
                    <div className="detail-item full-width">
                      <span className="detail-label">Details</span>
                      <span className="detail-value muted">No bank details available</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="detail-section">
                <h4><Calendar size={16} /> Timeline</h4>
                <div className="timeline">
                  <div className="timeline-item">
                    <span className="timeline-dot"></span>
                    <span className="timeline-label">Requested</span>
                    <span className="timeline-value">
                      {selectedWithdrawal.requested_at
                        ? format(new Date(selectedWithdrawal.requested_at), 'PPpp')
                        : '—'}
                    </span>
                  </div>
                  {selectedWithdrawal.processed_at && (
                    <div className="timeline-item">
                      <span className="timeline-dot completed"></span>
                      <span className="timeline-label">Processed</span>
                      <span className="timeline-value">{format(new Date(selectedWithdrawal.processed_at), 'PPpp')}</span>
                    </div>
                  )}
                  {selectedWithdrawal.updated_at && (
                    <div className="timeline-item">
                      <span className="timeline-dot"></span>
                      <span className="timeline-label">Updated</span>
                      <span className="timeline-value">{format(new Date(selectedWithdrawal.updated_at), 'PPpp')}</span>
                    </div>
                  )}
                </div>
              </div>

              {(selectedWithdrawal.rejection_reason || selectedWithdrawal.failure_reason) && (
                <div className="detail-section rejection">
                  <h4><Ban size={16} /> {selectedWithdrawal.status === 'failed' ? 'Failure Reason' : 'Rejection Reason'}</h4>
                  <p className="rejection-text">{selectedWithdrawal.rejection_reason || selectedWithdrawal.failure_reason}</p>
                </div>
              )}

              {selectedWithdrawal.status === 'pending' && (
                <div className="modal-actions">
                  <button
                    onClick={() => {
                      setSelectedWithdrawal(null);
                      setShowConfirmApprove(selectedWithdrawal);
                    }}
                    className="approve-btn"
                    disabled={processingId === selectedWithdrawal.withdrawal_id}
                  >
                    <CheckCircle size={18} />
                    Approve Withdrawal
                  </button>
                  <button
                    onClick={() => {
                      setShowRejectModal(true);
                    }}
                    className="reject-btn"
                  >
                    <XCircle size={18} />
                    Reject
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedWithdrawal && (
        <div className="modal-overlay" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
          <div className="modal-content reject-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reject Withdrawal</h3>
              <button className="modal-close" onClick={() => { setShowRejectModal(false); setRejectReason(''); }}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="reject-warning">
                You are about to reject a withdrawal of{' '}
                <strong>{formatAmount(selectedWithdrawal.amount, selectedWithdrawal.currency_code)}</strong>
                {selectedWithdrawal.user_full_name && <> for <strong>{selectedWithdrawal.user_full_name}</strong></>}.
              </p>
              <div className="form-group">
                <label>Rejection Reason *</label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Please provide a reason for rejection..."
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }} className="cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleReject}
                className="confirm-reject-btn"
                disabled={!rejectReason.trim() || processingId === selectedWithdrawal.withdrawal_id}
              >
                {processingId === selectedWithdrawal.withdrawal_id ? (
                  <RefreshCw size={18} className="spin" />
                ) : (
                  <XCircle size={18} />
                )}
                Confirm Rejection
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Withdrawals;
